import { computed } from 'vue'
import { defineStore } from 'pinia'
import { useApi } from '@/api/useApi'
import { useBaseStore } from '@haskoning/vue-basestore'
import API from '@/api/apiUrls'
import type { ChartConfiguration } from '@/stores/type/chartConfiguration.type'
import {
  DEFAULT_CHART_AXIS_GROUPING,
  DEFAULT_CHART_Y_MEASURE,
  DEFAULT_DISPLAY_MODE,
  DEFAULT_SORT_ORDER,
  DEFAULT_SELECTED_PHASES
} from '@/constants/chartConfiguration'

const serviceURL = API.CALCULATION.CHART_CONFIGURATIONS.INDEX

export const NEW_CHART_CONFIGURATION_ID = 0

export const DEFAULT_CHART_CONFIGURATION: Omit<ChartConfiguration, 'id' | 'calculation'> = {
  name: 'Chart',
  order: 0,
  x_axis_title: 'Location',
  y_axis_title: 'MKI [euro]',
  x_axis_grouping: DEFAULT_CHART_AXIS_GROUPING,
  y_axis_measure: DEFAULT_CHART_Y_MEASURE,
  selected_phases: [...DEFAULT_SELECTED_PHASES],
  display_mode: DEFAULT_DISPLAY_MODE,
  sort_order: DEFAULT_SORT_ORDER,
  filter_material_categories: [],
  row_overrides: []
}

export const createNewChartConfigurationPlaceholder = (): ChartConfiguration => ({
  id: NEW_CHART_CONFIGURATION_ID,
  calculation: 0,
  name: '',
  order: DEFAULT_CHART_CONFIGURATION.order,
  x_axis_title: '',
  y_axis_title: '',
  x_axis_grouping: '',
  y_axis_measure: '',
  selected_phases: [...DEFAULT_SELECTED_PHASES],
  display_mode: DEFAULT_DISPLAY_MODE,
  sort_order: DEFAULT_SORT_ORDER,
  filter_material_categories: [],
  row_overrides: []
})

export const useChartConfigurationStore = defineStore('chartConfiguration', () => {
  const { api } = useApi()
  const baseStore = useBaseStore<ChartConfiguration>({ service: serviceURL, api })

  const currentConfiguration = computed(() => baseStore.currentItem.value)

  const fetchByCalculation = async (calculationId: number): Promise<ChartConfiguration[]> => {
    const result = await baseStore.fetchItems({ calculation: calculationId })
    return Array.isArray(result) ? result : []
  }

  const ensureConfiguration = async (calculationId: number): Promise<ChartConfiguration | null> => {
    const existing = await fetchByCalculation(calculationId)
    if (existing.length > 0) {
      baseStore.setCurrentItem(existing[0])
      return existing[0]
    }
    const created = await baseStore.create({
      calculation: calculationId,
      ...DEFAULT_CHART_CONFIGURATION
    })
    if (created && !Array.isArray(created)) {
      baseStore.setCurrentItem(created)
      return created
    }
    return null
  }

  const updateConfiguration = async (
    item: ChartConfiguration
  ): Promise<ChartConfiguration | null> => {
    const updated = await baseStore.update(item)
    if (updated && typeof updated !== 'boolean') {
      baseStore.setCurrentItem(updated)
      return updated
    }
    return null
  }

  return {
    ...baseStore,
    currentConfiguration,
    fetchByCalculation,
    ensureConfiguration,
    updateConfiguration
  }
})
